// MyConsumptionStatsModal.jsx — "Mes statistiques" : combien LE CLIENT a généré et consommé
// en Ariary (vidéo + image), filtrable jour/semaine/mois/période personnalisée. Version
// client (scope à son propre compte) du dashboard admin équivalent - demande explicite de
// Hery le 2026-08-20 : "sur chaque compte pour chaque client", pas seulement côté admin.

import React, { useState, useEffect, useCallback } from 'react';
import { X, Coins, Film, ImageIcon, Loader2, Calendar, RefreshCw, TrendingDown, TrendingUp, BarChart3 } from 'lucide-react';
import { API_URL } from '../../config';

const toISODate = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();

const RANGE_PRESETS = [
  { id: 'today', label: "Aujourd'hui", range: () => { const t = today(); return [toISODate(t), toISODate(t)]; } },
  {
    id: 'week', label: 'Cette semaine',
    range: () => {
      const t = today();
      const day = (t.getDay() + 6) % 7;
      const monday = new Date(t); monday.setDate(t.getDate() - day);
      return [toISODate(monday), toISODate(t)];
    },
  },
  {
    id: 'month', label: 'Ce mois',
    range: () => { const t = today(); return [toISODate(new Date(t.getFullYear(), t.getMonth(), 1)), toISODate(t)]; },
  },
  { id: 'custom', label: 'Personnalisé', range: null },
];

const EMPTY = { count: 0, charged_ar: 0, refunded_ar: 0, net_ar: 0 };

function StatCard({ icon: Icon, title, count, netAr, gradient }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-4 text-white bg-gradient-to-br ${gradient}`}>
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="font-bold text-sm">{title}</div>
        </div>
        <div className="text-2xl font-black">{netAr.toLocaleString('fr-FR')} <span className="text-sm font-bold">Ar</span></div>
        <div className="text-white/80 text-xs mt-0.5">{count} génération{count > 1 ? 's' : ''}</div>
      </div>
    </div>
  );
}

export default function MyConsumptionStatsModal({ onClose }) {
  const token = localStorage.getItem('auth_token');
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(toISODate(today()));
  const [customTo, setCustomTo] = useState(toISODate(today()));
  const [video, setVideo] = useState(EMPTY);
  const [image, setImage] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeRange = useCallback(() => {
    const p = RANGE_PRESETS.find(r => r.id === preset);
    if (p && p.range) return p.range();
    return [customFrom, customTo];
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [from, to] = activeRange();
      const res = await fetch(`${API_URL}/api/ia-studio/my-consumption-stats?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) { setVideo(data.video); setImage(data.image); }
      else setError(data.error || 'Erreur de chargement');
    } catch (e) {
      setError('Erreur réseau');
    }
    setLoading(false);
  }, [activeRange, token]);

  useEffect(() => { load(); }, [load]);

  const totalCount = video.count + image.count;
  const totalNet = video.net_ar + image.net_ar;
  const [from, to] = activeRange();

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#140a24] border border-purple-500/25 rounded-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-300" /> Mes statistiques
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {RANGE_PRESETS.map(p => (
            <button
              key={p.id} onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                preset === p.id ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={load} disabled={loading} className="ml-auto w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center disabled:opacity-40">
            {loading ? <Loader2 className="w-3.5 h-3.5 text-gray-300 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 text-gray-300" />}
          </button>
        </div>

        {preset === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <label className="text-gray-400 font-semibold">Du</label>
            <input
              type="date" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white focus:outline-none focus:border-amber-400"
            />
            <label className="text-gray-400 font-semibold">Au</label>
            <input
              type="date" value={customTo} min={customFrom} max={toISODate(today())} onChange={e => setCustomTo(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white focus:outline-none focus:border-amber-400"
            />
          </div>
        )}
        <p className="text-gray-500 text-[11px] mb-4">
          Période : <span className="text-gray-300 font-semibold">{from}</span> → <span className="text-gray-300 font-semibold">{to}</span>
        </p>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-xs mb-3">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : (
          <>
            <div className="mb-3">
              <StatCard icon={Coins} title="Total consommé" count={totalCount} netAr={totalNet} gradient="from-amber-500 via-orange-500 to-rose-600" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon={Film} title="Vidéo" count={video.count} netAr={video.net_ar} gradient="from-fuchsia-600 via-purple-600 to-indigo-700" />
              <StatCard icon={ImageIcon} title="Image" count={image.count} netAr={image.net_ar} gradient="from-cyan-500 via-blue-600 to-indigo-700" />
            </div>

            {(video.refunded_ar > 0 || image.refunded_ar > 0) && (
              <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 text-[11px] text-gray-400 flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                <span>
                  {(video.refunded_ar + image.refunded_ar).toLocaleString('fr-FR')} Ar remboursés (générations échouées) sur cette période.
                </span>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span>{(video.charged_ar + image.charged_ar).toLocaleString('fr-FR')} Ar facturés au total sur cette période.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
